import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import './Contact.css';

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

const Contact: React.FC = () => {
  const { t } = useLanguage();
  const { contact } = t;

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = contact.errName;
    if (!formData.email.trim()) {
      newErrors.email = contact.errEmailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = contact.errEmailInvalid;
    }
    if (!formData.subject.trim()) newErrors.subject = contact.errSubject;
    if (!formData.message.trim()) newErrors.message = contact.errMessage;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 1500);
  };

  return (
    <div className="contact">

      {/* ── Page Hero ─────────────────────────────────────── */}
      <section className="page-hero">
        <div className="page-hero__overlay" />
        <div className="page-hero__content container">
          <div className="section-label section-label--light">{contact.pageLabel}</div>
          <h1 className="page-hero__headline">{contact.heroTitle}</h1>
          <p className="page-hero__sub">{contact.heroSub}</p>
        </div>
      </section>

      {/* ── Contact Body ──────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <div className="contact__grid">

            {/* Info Panel */}
            <div className="contact__info">
              <h2 className="contact__info-title">{contact.infoTitle}</h2>

              <div className="contact__info-block">
                <div className="contact__info-icon">✉</div>
                <div>
                  <h4>{contact.infoGeneral}</h4>
                  <a href="mailto:info@mahmedix.de">info@mahmedix.de</a>
                </div>
              </div>

              <div className="contact__info-block">
                <div className="contact__info-icon">☎</div>
                <div>
                  <h4>{contact.infoPhone}</h4>
                  <a href="tel:+4938344398063">(+49) 38344398063</a>
                </div>
              </div>

              <div className="contact__info-block">
                <div className="contact__info-icon">📍</div>
                <div>
                  <h4>{contact.infoAddress}</h4>
                  <address>
                    {contact.infoAddressLines.map((line, i) => (
                      <React.Fragment key={i}>{line}{i < contact.infoAddressLines.length - 1 && <br />}</React.Fragment>
                    ))}
                  </address>
                </div>
              </div>

              <div className="contact__hours">
                <h4>{contact.infoHoursTitle}</h4>
                <p>{contact.infoHoursValue}</p>
              </div>

              <div className="contact__social-impact">
                <h4>{contact.infoImpactTitle}</h4>
                <a href="mailto:socialimpact@mahmedix.com">
                  socialimpact@mahmedix.com
                </a>
              </div>
            </div>

            {/* Form */}
            <div className="contact__form-wrapper">
              <h2 className="contact__form-title">{contact.formTitle}</h2>

              {submitted ? (
                <div className="contact__success">
                  <div className="contact__success-icon">✅</div>
                  <h3>{contact.successTitle}</h3>
                  <p>{contact.successBody}</p>
                  <button
                    className="btn btn--primary"
                    onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', phone: '', subject: '', message: '' }); }}
                  >
                    {contact.successBtn}
                  </button>
                </div>
              ) : (
                <form className="contact__form" onSubmit={handleSubmit} noValidate>
                  <div className="form-row">
                    <div className={`form-group ${errors.name ? 'form-group--error' : ''}`}>
                      <label htmlFor="name">{contact.formName} <span className="form-required">*</span></label>
                      <input
                        id="name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={contact.formNamePlaceholder}
                        autoComplete="name"
                      />
                      {errors.name && <span className="form-error-msg">{errors.name}</span>}
                    </div>

                    <div className={`form-group ${errors.email ? 'form-group--error' : ''}`}>
                      <label htmlFor="email">{contact.formEmail} <span className="form-required">*</span></label>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={contact.formEmailPlaceholder}
                        autoComplete="email"
                      />
                      {errors.email && <span className="form-error-msg">{errors.email}</span>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">{contact.formPhone}</label>
                      <input
                        id="phone"
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder={contact.formPhonePlaceholder}
                        autoComplete="tel"
                      />
                    </div>

                    <div className={`form-group ${errors.subject ? 'form-group--error' : ''}`}>
                      <label htmlFor="subject">{contact.formSubject} <span className="form-required">*</span></label>
                      <input
                        id="subject"
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder={contact.formSubjectPlaceholder}
                      />
                      {errors.subject && <span className="form-error-msg">{errors.subject}</span>}
                    </div>
                  </div>

                  <div className={`form-group ${errors.message ? 'form-group--error' : ''}`}>
                    <label htmlFor="message">{contact.formMessage} <span className="form-required">*</span></label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={contact.formMessagePlaceholder}
                      rows={6}
                    />
                    {errors.message && <span className="form-error-msg">{errors.message}</span>}
                  </div>

                  <button
                    type="submit"
                    className={`btn btn--primary btn--lg contact__submit ${submitting ? 'contact__submit--loading' : ''}`}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="contact__spinner" />
                        {contact.formSending}
                      </>
                    ) : (
                      contact.formSend
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Map ───────────────────────────────────────────── */}
      <section className="contact__map-section">
        <div className="container">
          <h2 className="contact__map-title">{contact.mapTitle}</h2>
        </div>
        <div className="contact__map-wrapper">
          <iframe
            title="MahMedix Office Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2380.5!2d13.3832!3d54.0887!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47ac8ce6ae2b7e4f%3A0x1234567890abcdef!2sErnst-Th%C3%A4lmann-Ring%2064a%2C%2017491%20Greifswald%2C%20Germany!5e0!3m2!1sen!2sde!4v1700000000000"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>

    </div>
  );
};

export default Contact;
